import './components/calculators/format';
import './components/calculators/input';

// =====================================================================
//  CAR & MOTORCYCLE INSURANCE — premium estimate
//  Static markup lives in page-calculator-car-insurance.php.
//  Rate matrix comes from the page as DB-editable JSON (Calc.data.carInsurance):
//  vehicle -> region -> cover, each table declaring its own `model`.
// =====================================================================
(function () {
  const fmt = Calc.format;

  const DATA = (window.Calc && Calc.data && Calc.data.carInsurance) ? Calc.data.carInsurance : {};
  const VEHICLES    = DATA.vehicles || {};
  const USE_LOADING = DATA.useLoading || {};
  const MIN_PREMIUM = +DATA.minPremium || 0;
  const STAMP_DUTY  = +DATA.stampDuty  || 0;
  const SST_RATE    = +DATA.sstRate    || 0;

  // Reference-grid row axes: sum insured for tariff models, engine capacity for
  // flat ones (where sum insured makes no difference and would repeat itself).
  const REF_VALUES = [20000, 50000, 80000, 100000, 150000, 200000];
  // One row per cc band so every band is represented in the flat-model grid.
  const REF_CCS    = { car: [1400, 1650, 2200, 3050, 4100, 5000],
                       motorcycle: [50, 100, 125, 225, 350, 500, 1000] };
  // Only the columns actually on screen: syncNcdOptions() hides the tiers the
  // selected vehicle's ladder doesn't use, and rendering a cell for a hidden
  // header would shift every column out of alignment.
  const refNcds = () =>
    [...document.querySelectorAll('.ref-grid thead th[data-ncd]')]
      .filter(th => !th.classList.contains('d-none'))
      .map(th => +th.dataset.ncd);

  const state = {
    vehicle: 'car',
    cover: 'comprehensive',
    use: 'private',
    region: 'peninsular',
    value: 50000,
    cc: 1500,
    ncd: 0.55,
    rebate: 0,          // percent, e.g. 10
    activeTab: 'breakdown',
  };

  const setText = (id, val) => { const el = document.getElementById(id); if (el) el.textContent = val; };
  const setVal = (sel, val) => { const el = document.querySelector(sel); if (el) el.textContent = val; };
  const toggleAll = (sel, hide) => document.querySelectorAll(sel).forEach(el => el.classList.toggle('d-none', hide));
  const hide = (id, on) => { const el = document.getElementById(id); if (el) el.classList.toggle('d-none', on); };
  const syncInput = (id, val) => Calc.input.syncValue(id, val);
  const pct = (r) => `${+(r * 100).toFixed(2)}%`;   // 0.3833 → "38.33%", 0.55 → "55%"

  // ---- rate lookup ---------------------------------------------------------
  const vehicleCfg = () => VEHICLES[state.vehicle] || {};
  const table = () => {
    const regions = vehicleCfg().regions || {};
    return (regions[state.region] || {})[state.cover] || null;
  };
  const ncdLadder = () => {
    const l = vehicleCfg().ncd;
    return Array.isArray(l) && l.length ? l : [0];
  };
  const bandFor = (bands, cc) =>
    bands.find(b => b.maxCc === null || b.maxCc === undefined || cc <= b.maxCc)
    || bands[bands.length - 1] || null;

  // ---- premium ------------------------------------------------------------
  // Motor-tariff math — kept local (finance.js is for SHARED formulas only).
  //   model 'tariff': basic = first1000(cc) + ceil((SI-1000)/1000) x per1000
  //                   ("each RM1,000 or part thereof" — a ceiling, not a share)
  //   model 'flat'  : basic = band premium; the sum insured is ignored
  // then use loading, NCD, insurer discount, service tax, stamp duty (untaxed).
  const ZERO = { first: 0, additional: 0, base: 0, saving: 0, afterNcd: 0,
                 rebate: 0, afterRebate: 0, sst: 0, stamp: 0, total: 0 };

  function premium(opts) {
    const t = table();
    if (!t || !Array.isArray(t.bands) || !t.bands.length) return Object.assign({}, ZERO);
    const cc = opts.cc, si = Math.max(opts.value || 0, 0);
    // A blank cc must not fall through to the smallest band and price a vehicle
    // with no engine capacity entered yet.
    if (!(cc > 0)) return Object.assign({}, ZERO);
    const isTariff = t.model === 'tariff';
    // Flat cover ignores the sum insured, so an empty one is not a blocker there.
    if (isTariff && si <= 0) return Object.assign({}, ZERO);

    const band = bandFor(t.bands, cc);
    if (!band) return Object.assign({}, ZERO);

    const first = +band.premium || 0;
    const additional = isTariff
      ? Math.max(Math.ceil((si - 1000) / 1000), 0) * (+t.per1000 || 0)
      : 0;
    const loading = +USE_LOADING[opts.use] || 1;
    const base = (first + additional) * loading;

    const saving = base * opts.ncd;
    const afterNcd = Math.max(base - saving, MIN_PREMIUM);
    const rebate = afterNcd * (opts.rebate / 100);
    const afterRebate = afterNcd - rebate;
    const sst = afterRebate * SST_RATE;
    return { first, additional, base, saving, afterNcd, rebate, afterRebate,
             sst, stamp: STAMP_DUTY, total: afterRebate + sst + STAMP_DUTY };
  }
  const current = () => premium(state);

  function updateStatBand() {
    const p = current();
    setText('ci-stat-total',  fmt.money(p.total, 0));
    setText('ci-stat-base',   fmt.money(p.base, 0));
    setText('ci-stat-saving', fmt.money(p.saving, 0));
    setText('ci-stat-tax',    fmt.money(p.sst + p.stamp, 0));
  }

  function updateBreakdown() {
    const p = current();
    const isTariff = (table() || {}).model === 'tariff';
    setText('ci-r-sum',          fmt.money(state.value));
    setText('ci-r-cc',           fmt.number(state.cc, 0));
    setText('ci-r-first',        fmt.money(p.first));
    setText('ci-r-additional',   fmt.money(p.additional));
    setText('ci-r-base',         fmt.money(p.base));
    setText('ci-r-ncd',          pct(state.ncd));
    setText('ci-r-saving',       fmt.money(p.saving));
    setText('ci-r-after-ncd',    fmt.money(p.afterNcd));
    setText('ci-r-rebate',       fmt.money(p.rebate));
    setText('ci-r-after-rebate', fmt.money(p.afterRebate));
    setText('ci-r-sst',          fmt.money(p.sst));
    setText('ci-r-stamp',        fmt.money(p.stamp));
    setText('ci-r-total',        fmt.money(p.total));

    // Rows for steps that don't apply are hidden rather than shown as zero.
    hide('ci-r-sum-row',          !isTariff);
    hide('ci-r-first-row',        !isTariff);
    hide('ci-r-additional-row',   !isTariff);
    hide('ci-r-rebate-row',       state.rebate <= 0);
    hide('ci-r-after-rebate-row', state.rebate <= 0);
  }

  function updateReferenceGrid() {
    setVal('.ci-grid-cc-num', fmt.number(state.cc, 0));
    const tbody = document.getElementById('ci-grid-tbody');
    if (!tbody) return;
    const isTariff = (table() || {}).model === 'tariff';
    const ncds = refNcds();
    const rows = isTariff ? REF_VALUES : (REF_CCS[state.vehicle] || REF_CCS.car);

    tbody.innerHTML = rows.map(rowVal => {
      const isHighRow = isTariff ? rowVal === state.value : rowVal === state.cc;
      const cells = ncds.map(ncd => {
        const p = premium(Object.assign({}, state, {
          ncd,
          value: isTariff ? rowVal : state.value,
          cc:    isTariff ? state.cc : rowVal,
        }));
        const isHighCol = Math.abs(ncd - state.ncd) < 0.0001;
        return `<td class="${isHighCol ? 'highlight-col' : ''}">${fmt.number(p.total, 0)}</td>`;
      }).join('');
      const label = isTariff ? `RM ${rowVal.toLocaleString()}` : rowVal.toLocaleString();
      return `<tr class="${isHighRow ? 'highlight' : ''}"><th>${label}</th>${cells}</tr>`;
    }).join('');
  }

  // Keep the NCD select and the grid's NCD columns to the ladder the selected
  // vehicle actually uses — cars reach 55%, motorcycles cap far lower.
  function syncNcdOptions() {
    const ladder = ncdLadder();
    const allowed = (v) => ladder.some(l => Math.abs(l - v) < 0.0001);
    document.querySelectorAll('#ci-ncd-input option').forEach(opt => {
      opt.hidden = !allowed(parseFloat(opt.value));
    });
    document.querySelectorAll('.ref-grid thead th[data-ncd]').forEach(th => {
      th.classList.toggle('d-none', !allowed(+th.dataset.ncd));
    });
    // Switching vehicle can strand the selection above the new ladder's cap.
    if (!allowed(state.ncd)) state.ncd = ladder[ladder.length - 1];
    const sel = document.getElementById('ci-ncd-input');
    if (sel) sel.value = ladder.reduce((best, l) =>
      Math.abs(l - state.ncd) < Math.abs(best - state.ncd) ? l : best, ladder[0]);
  }

  function refresh() {
    syncInput('ci-value-input', state.value);
    syncInput('ci-cc-input', state.cc);
    syncInput('ci-rebate-input', state.rebate);
    // NCD is a user-driven <select>; syncNcdOptions owns its value.

    const t = table() || {};
    const isTariff = t.model === 'tariff';

    [['#ci-vehicle-tabs button', 'vehicle'], ['#ci-cover-tabs button', 'cover'],
     ['#ci-use-tabs button', 'use'], ['#ci-region-tabs button', 'region'],
    ].forEach(([sel, key]) => {
      document.querySelectorAll(sel).forEach(btn => {
        const active = btn.dataset[key] === state[key];
        btn.classList.toggle('btn-primary', active);
        btn.classList.toggle('btn-outline-primary', !active);
      });
    });

    syncNcdOptions();

    // Sum insured is meaningless under flat-rated cover — switch it off rather
    // than let the page imply it changes the premium.
    const valueInput = document.getElementById('ci-value-input');
    if (valueInput) valueInput.disabled = !isTariff;
    document.getElementById('ci-value-field')?.classList.toggle('opacity-50', !isTariff);

    hide('ci-note-thirdparty', isTariff);
    hide('ci-note-estimate', t.source !== 'market-estimate');
    toggleAll('.ci-ref-intro-tariff', !isTariff);
    toggleAll('.ci-ref-intro-flat', isTariff);
    toggleAll('.ci-grid-axis-value', !isTariff);
    toggleAll('.ci-grid-axis-cc', isTariff);

    updateStatBand();
    if (state.activeTab === 'breakdown') updateBreakdown();
    else updateReferenceGrid();
  }

  function activateTab(tabId) {
    state.activeTab = tabId;
    document.querySelectorAll('#ci-tabs .btn-tab').forEach(btn => {
      btn.classList.toggle('active', btn.dataset.tab === tabId);
    });
    document.querySelectorAll('.tab-panel').forEach(panel => {
      panel.classList.toggle('d-none', panel.id !== `ci-tab-${tabId}`);
    });
    if (tabId === 'breakdown') updateBreakdown();
    else updateReferenceGrid();
  }

  function bindEvents() {
    ['input', 'change'].forEach(evt => document.addEventListener(evt, (e) => {
      const id = e.target.id;
      if (id === 'ci-value-input')  { state.value  = Calc.input.getValue(e.target) || 0; refresh(); }
      if (id === 'ci-cc-input')     { state.cc     = Calc.input.getValue(e.target) || 0; refresh(); }
      if (id === 'ci-rebate-input') { state.rebate = Calc.input.getValue(e.target) || 0; refresh(); }
      if (id === 'ci-ncd-input')    { state.ncd    = parseFloat(e.target.value) || 0;    refresh(); }
    }));

    [['#ci-vehicle-tabs button', 'vehicle'], ['#ci-cover-tabs button', 'cover'],
     ['#ci-use-tabs button', 'use'], ['#ci-region-tabs button', 'region'],
    ].forEach(([sel, key]) => {
      document.querySelectorAll(sel).forEach(btn =>
        btn.addEventListener('click', () => { state[key] = btn.dataset[key]; refresh(); }));
    });

    document.querySelectorAll('#ci-tabs .btn-tab').forEach(btn => {
      btn.addEventListener('click', () => activateTab(btn.dataset.tab));
    });
  }

  document.addEventListener('DOMContentLoaded', () => {
    bindEvents();
    refresh();
  });
})();
